import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import './Navigation.css';

const NAV_LINKS = [
  { to: '/',           label: 'Início'     },
  { to: '/blog',       label: 'Blog'       },
  { to: '/forum',      label: 'Fórum'      },
  { to: '/produtos',   label: 'Cursos'     },
  { to: '/atividades', label: 'Atividades' },
  { to: '/sobre',      label: 'Sobre'      },
];

// ── Busca com debounce ─────────────────────────────────────
function useSearch(term) {
  const [results,  setResults]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const debounce   = useRef(null);

  useEffect(() => {
    if (!term.trim() || term.length < 2) { setResults([]); return; }
    setLoading(true);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const res  = await fetch(`/posts/search?term=${encodeURIComponent(term)}`, { credentials: 'include' });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
      } catch { setResults([]); }
      setLoading(false);
    }, 380);
    return () => clearTimeout(debounce.current);
  }, [term]);

  return { results, loading };
}

export default function Navigation() {
  const { user }       = useAuth();
  const { dark, toggle } = useTheme();
  const location   = useLocation();
  const navigate   = useNavigate();
  const [open,         setOpen]         = useState(false);
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [streak,       setStreak]       = useState(0);
  const searchRef  = useRef(null);
  const inputRef   = useRef(null);

  const { results, loading } = useSearch(searchTerm);

  function close()        { setOpen(false); }
  function closeSearch()  { setSearchOpen(false); setSearchTerm(''); }

  // Abre o campo e foca
  function openSearch() {
    setSearchOpen(true);
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 80);
  }

  // Fecha ao clicar fora
  useEffect(() => {
    if (!searchOpen) return;
    function onClick(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) closeSearch();
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [searchOpen]);

  // Fecha ao trocar de rota
  useEffect(() => { closeSearch(); close(); }, [location.pathname]); // eslint-disable-line

  // Contagem de notificações não lidas
  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    fetch('/api/user/notifications', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setUnreadCount(data.filter(n => !n.lida).length);
      })
      .catch(() => {});
  }, [user, location.pathname]);

  // Streak diário — registra visita e exibe streak
  useEffect(() => {
    if (!user) { setStreak(0); return; }
    fetch('/api/user/streak/registrar', { method: 'POST', credentials: 'include' }).catch(() => {});
    fetch('/api/user/streak', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (data.streak !== undefined) setStreak(data.streak); })
      .catch(() => {});
  }, [user]);

  function handleResultClick(item) {
    closeSearch();
    if (item.tipo === 'produto') {
      navigate(`/produto/${item.id}`);
    } else {
      navigate('/blog', { state: { openPostId: item.id } });
    }
  }

  const produtos = results.filter(r => r.tipo === 'produto');
  const posts    = results.filter(r => r.tipo === 'post');

  return (
    <header className="nav-header" style={{ position: 'relative' }}>

      {/* Logo */}
      <Link to="/" className="nav-logo" onClick={close}>
        <img src="/assets/logo_light.png" alt="Inglês do Dionísio" />
        <span className="nav-logo-name">Inglês do Dionísio</span>
      </Link>

      {/* Botão de busca */}
      <button className="nav-search-btn" onClick={openSearch} title="Buscar" aria-label="Buscar">
        <i className="fas fa-search" />
      </button>

      {/* Dark mode toggle */}
      <button className="nav-theme-btn" onClick={toggle} title={dark ? 'Modo claro' : 'Modo escuro'} aria-label="Alternar tema">
        <i className={dark ? 'fas fa-sun' : 'fas fa-moon'} />
      </button>

      {/* Sino de notificações */}
      {user && (
        <Link
          to="/usuarios"
          state={{ section: 'notificacoes' }}
          className="nav-bell-btn"
          title="Notificações"
          aria-label="Notificações"
          onClick={close}
        >
          <i className="fas fa-bell" />
          {unreadCount > 0 && (
            <span className="nav-bell-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </Link>
      )}

      {/* Streak diário */}
      {user && streak > 0 && (
        <div
          title={`Sequência de ${streak} dia${streak !== 1 ? 's' : ''}`}
          style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'rgba(255,140,0,0.18)', borderRadius: 20, padding: '4px 11px', cursor: 'default', marginRight: 2 }}
        >
          <span style={{ fontSize: '1rem' }}>🔥</span>
          <span style={{ color: '#ffa94d', fontWeight: 700, fontSize: '0.9rem', fontFamily: 'Poppins,sans-serif' }}>{streak}</span>
        </div>
      )}

      {/* Hamburger */}
      <button
        className={`nav-ham${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Menu"
        aria-expanded={open}
      >
        <span /><span /><span />
      </button>

      {/* Links */}
      <nav className={`nav-links${open ? ' open' : ''}`}>
        {NAV_LINKS.map(item => (
          <Link
            key={item.to}
            to={item.to}
            className="nav-link"
            style={location.pathname === item.to
              ? { background: 'rgba(255,255,255,0.22)', fontWeight: 700 }
              : {}}
            onClick={close}
          >
            {item.label}
          </Link>
        ))}

        {user ? (
          <>
            {!!user.isAdmin && (
              <>
                <Link to="/admin-blog"    className="nav-link" onClick={close}>
                  <i className="fas fa-pen-nib" style={{ marginRight: 4 }} /> Blog Admin
                </Link>
                <Link to="/admin-textos"  className="nav-link" onClick={close}>
                  <i className="fas fa-book-reader" style={{ marginRight: 4 }} /> Textos Admin
                </Link>
                <Link to="/admin-alunos"   className="nav-link" onClick={close}>
                  <i className="fas fa-graduation-cap" style={{ marginRight: 4 }} /> Alunos
                </Link>
                <Link to="/admin-dialogos" className="nav-link" onClick={close}>
                  <i className="fas fa-comments" style={{ marginRight: 4 }} /> Diálogos
                </Link>
                <Link to="/admin-pronuncia" className="nav-link" onClick={close}>
                  <i className="fas fa-microphone-alt" style={{ marginRight: 4 }} /> Pronúncia
                </Link>
                <Link to="/admin-verbos" className="nav-link" onClick={close}>
                  <i className="fas fa-book" style={{ marginRight: 4 }} /> Verbos
                </Link>
              </>
            )}
            <Link to="/usuarios" className="nav-link-btn" onClick={close}>
              <i className="fas fa-user" style={{ marginRight: 6 }} />
              Meu Perfil
            </Link>
          </>
        ) : (
          <>
            <Link to="/login"    className="nav-link" onClick={close}>Login</Link>
            <Link to="/register" className="nav-link-btn" onClick={close}>Cadastrar</Link>
          </>
        )}
      </nav>

      {/* Overlay de busca */}
      {searchOpen && (
        <div className="nav-search-overlay" ref={searchRef}>
          <div className="nav-search-input-wrap">
            <i className="fas fa-search" />
            <input
              ref={inputRef}
              className="nav-search-input"
              placeholder="Buscar cursos, blog..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && closeSearch()}
            />
            {searchTerm && (
              <button className="nav-search-close" onClick={() => setSearchTerm('')}>
                <i className="fas fa-times" />
              </button>
            )}
            <button className="nav-search-close" onClick={closeSearch}>
              <i className="fas fa-arrow-up" />
            </button>
          </div>

          {/* Resultados */}
          {searchTerm.length >= 2 && (
            <div className="nav-search-results">
              {loading && <div className="nav-search-loading"><i className="fas fa-spinner fa-spin" /> Buscando...</div>}

              {!loading && results.length === 0 && (
                <div className="nav-search-empty">Nenhum resultado para "<strong>{searchTerm}</strong>"</div>
              )}

              {!loading && produtos.length > 0 && (
                <>
                  <div className="nav-search-group-title"><i className="fas fa-box" style={{ marginRight: 5 }} />Cursos & Produtos</div>
                  {produtos.map(item => (
                    <div key={item.id} className="nav-search-item" onClick={() => handleResultClick(item)}>
                      <div className="nav-search-item-icon produto"><i className="fas fa-graduation-cap" /></div>
                      <div>
                        <div className="nav-search-item-name">{item.nome}</div>
                        <div className="nav-search-item-type">Produto</div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {!loading && posts.length > 0 && (
                <>
                  <div className="nav-search-group-title"><i className="fas fa-newspaper" style={{ marginRight: 5 }} />Artigos do Blog</div>
                  {posts.map(item => (
                    <div key={item.id} className="nav-search-item" onClick={() => handleResultClick(item)}>
                      <div className="nav-search-item-icon post"><i className="fas fa-file-alt" /></div>
                      <div>
                        <div className="nav-search-item-name">{item.nome}</div>
                        <div className="nav-search-item-type">Blog</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

    </header>
  );
}
